import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Order, Wallet, WalletType } from '../types';
import { PinInput } from '../components/auth/PinInput';
import { Logo } from '../components/shared/Logo';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import api, { formatCurrency } from '../services/api';
import './OrderPaymentScreen.css';

interface WalletPayment {
  walletId: string;
  amount: number;
}

export const OrderPaymentScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state?.order as Order;

  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletPayments, setWalletPayments] = useState<WalletPayment[]>([]);
  const [step, setStep] = useState<'payment' | 'pin' | 'success'>('payment');
  const [pinError, setPinError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadWallets = async () => {
      try {
        const walletsData = await api.wallets.getWallets();
        setWallets(walletsData);
      } catch (error) {
        console.error('Failed to load wallets:', error);
        // Handle error - maybe show error message or redirect
      }
    };

    loadWallets();
  }, []);

  // Redirect back if no order provided
  if (!order) {
    navigate('/orders');
    return null;
  }

  const getTotalPayment = () => {
    return walletPayments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const getRemainingAmount = () => {
    return order.totalAmount - getTotalPayment();
  };

  const updateWalletPayment = (walletId: string, amount: number) => {
    setWalletPayments(prev => {
      const existing = prev.find(p => p.walletId === walletId);
      if (existing) {
        if (amount <= 0) {
          return prev.filter(p => p.walletId !== walletId);
        }
        return prev.map(p => p.walletId === walletId ? { ...p, amount } : p);
      } else if (amount > 0) {
        return [...prev, { walletId, amount }];
      }
      return prev;
    });
  };

  const getWalletPayment = (walletId: string) => {
    return walletPayments.find(p => p.walletId === walletId)?.amount || 0;
  };

  // Per-order wallet restrictions (optional). When present, only the listed
  // wallet GUIDs may be used, each capped at its maxAmount.
  const walletLimits = order.allowedWallets && order.allowedWallets.length > 0 ? order.allowedWallets : null;
  const isWalletAllowed = (walletId: string) => !walletLimits || walletLimits.some(w => w.walletId === walletId);
  const walletCap = (walletId: string) => {
    const match = walletLimits?.find(w => w.walletId === walletId);
    return match ? match.maxAmount : Infinity;
  };
  const visibleWallets = wallets.filter(w => isWalletAllowed(w.id));

  const canProceed = () => {
    return getRemainingAmount() === 0 && getTotalPayment() > 0;
  };

  const handleProceedToPin = () => {
    if (canProceed()) {
      setStep('pin');
    }
  };

  const handlePinSubmit = async (enteredPin: string) => {
    setLoading(true);
    setPinError('');

    try {
      // Call the API to process the payment
      await api.orders.payOrder(order.id, walletPayments, enteredPin);
      setStep('success');
    } catch (err: any) {
      console.error('Payment failed:', err);
      if (err.response?.data?.message) {
        setPinError(err.response.data.message);
      } else {
        setPinError('Payment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Remove local formatCurrency function as we're importing it from api

  const getWalletIcon = (type: WalletType) => {
    switch (type) {
      case WalletType.MAIN:
        return 'fas fa-wallet';
      case WalletType.GRANT:
        return 'fas fa-gift';
      case WalletType.REWARDS:
        return 'fas fa-star';
      default:
        return 'fas fa-wallet';
    }
  };

  const getWalletColor = (type: WalletType) => {
    switch (type) {
      case WalletType.MAIN:
        return 'var(--fresh-green)';
      case WalletType.GRANT:
        return 'var(--banana)';
      case WalletType.REWARDS:
        return 'var(--bubblegum)';
      default:
        return 'var(--fresh-green)';
    }
  };

  if (step === 'success') {
    return (
      <div className="order-payment-screen">
        <div className="order-payment-container">
          <div className="screen-header">
            <div className="header-left">
              <div></div>
              <h1>Payment Complete!</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>
          
          <div className="screen-content">
            <div className="success-content">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h2>Order Paid Successfully!</h2>
              <div className="payment-summary">
                <div className="summary-item">
                  <span className="label">Order Number:</span>
                  <span className="value">#{order.orderNumber}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Store:</span>
                  <span className="value">{order.storeName}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Total Paid:</span>
                  <span className="value">{formatCurrency(order.totalAmount)}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Payment Method:</span>
                  <div className="payment-breakdown">
                    {walletPayments.map(payment => {
                      const wallet = wallets.find(w => w.id === payment.walletId);
                      return (
                        <div key={payment.walletId} className="breakdown-item">
                          <span>{wallet?.type} Wallet: {formatCurrency(payment.amount)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="success-actions">
                <button 
                  onClick={() => navigate('/orders')}
                  className="primary-button"
                >
                  View Orders
                </button>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="secondary-button"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'pin') {
    return (
      <div className="order-payment-screen">
        <div className="order-payment-container">
          <div className="screen-header">
            <div className="header-left">
              <button onClick={() => setStep('payment')} className="back-button">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h1>Confirm Payment</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>

          <div className="screen-content pin-content">
            <div className="payment-confirmation">
              <div className="order-summary">
                <h3>#{order.orderNumber}</h3>
                <p>{order.storeName}</p>
                <div className="amount-display">
                  <span className="currency">R</span>
                  <span className="amount">{order.totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <div className="payment-breakdown-summary">
                <h4>Payment from:</h4>
                {walletPayments.map(payment => {
                  const wallet = wallets.find(w => w.id === payment.walletId);
                  return (
                    <div key={payment.walletId} className="payment-method-item">
                      <div className="wallet-info">
                        <i className={getWalletIcon(wallet!.type)} style={{ color: getWalletColor(wallet!.type) }}></i>
                        <span>{wallet?.type} Wallet</span>
                      </div>
                      <span className="payment-amount">{formatCurrency(payment.amount)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pin-section">
              <h3>Enter your PIN to confirm</h3>
              {pinError && (
                <div className="error-message">
                  {pinError}
                </div>
              )}
              <PinInput
                length={4}
                onComplete={handlePinSubmit}
                error={!!pinError}
                disabled={loading}
              />
              {loading && (
                <div className="loading-message">
                  <i className="fas fa-spinner fa-spin"></i>
                  Processing payment...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-payment-screen">
      <div className="order-payment-container">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => navigate('/orders')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Pay Order</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>

        <div className="screen-content">
          {/* Order Details */}
          <div className="order-details-card">
            <div className="order-header">
              <h3>#{order.orderNumber}</h3>
              <p className="store-name">{order.storeName}</p>
            </div>
            <div className="order-items">
              <h4>Items ({order.items?.length || 0})</h4>
              {order.items?.map(item => (
                <div key={item.id} className="item-row">
                  <span className="item-name">{item.productName}</span>
                  <span className="item-details">
                    {item.quantity}x {formatCurrency(item.price)}
                  </span>
                </div>
              )) || (
                <div className="no-items">
                  <p>No item details available</p>
                </div>
              )}
            </div>
            <div className="order-total">
              <span className="total-label">Total Amount</span>
              <span className="total-amount">{formatCurrency(order.totalAmount)}</span>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="payment-summary-card">
            <h3>Payment Summary</h3>
            <div className="payment-progress">
              <div className="progress-item">
                <span className="label">Total Due:</span>
                <span className="value">{formatCurrency(order.totalAmount)}</span>
              </div>
              <div className="progress-item">
                <span className="label">Amount Allocated:</span>
                <span className="value">{formatCurrency(getTotalPayment())}</span>
              </div>
              <div className="progress-item remaining">
                <span className="label">Remaining:</span>
                <span className={`value ${getRemainingAmount() === 0 ? 'complete' : 'pending'}`}>
                  {formatCurrency(getRemainingAmount())}
                </span>
              </div>
            </div>
          </div>

          {/* Wallet Selection */}
          <div className="wallet-selection">
            <h3>Select Payment Methods</h3>
            <div className="wallets-list">
              {visibleWallets.length === 0 && (
                <p className="no-wallets-note">No eligible wallets for this order.</p>
              )}
              {visibleWallets.map(wallet => (
                <div key={wallet.id} className="wallet-payment-card">
                  <div className="wallet-info">
                    <div className="wallet-icon" style={{ color: getWalletColor(wallet.type) }}>
                      <i className={getWalletIcon(wallet.type)}></i>
                    </div>
                    <div className="wallet-details">
                      <h4>{wallet.type} Wallet</h4>
                      <p>Available: {formatCurrency(wallet.availableBalance)}</p>
                      {walletLimits && (
                        <p className="wallet-limit-note">Limit for this order: {formatCurrency(walletCap(wallet.id))}</p>
                      )}
                    </div>
                  </div>
                  <div className="payment-input">
                    <div className="amount-input-container">
                      <span className="currency-symbol">R</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={Math.min(wallet.availableBalance, walletCap(wallet.id), getRemainingAmount() + getWalletPayment(wallet.id))}
                        placeholder="0.00"
                        value={getWalletPayment(wallet.id) || ''}
                        onChange={(e) => {
                          const amount = parseFloat(e.target.value) || 0;
                          const maxAmount = Math.min(wallet.availableBalance, walletCap(wallet.id), getRemainingAmount() + getWalletPayment(wallet.id));
                          updateWalletPayment(wallet.id, Math.min(amount, maxAmount));
                        }}
                        className="amount-input"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Continue Button */}
          <div className="form-actions">
            <button 
              onClick={handleProceedToPin}
              disabled={!canProceed()}
              className="primary-button"
            >
              {getRemainingAmount() > 0 
                ? `Add ${formatCurrency(getRemainingAmount())} more`
                : 'Proceed to Payment'
              }
            </button>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};